import {useShopifyFetch} from "./useShopifyFetch";
import {useQuery} from "react-query";
import {useShop} from "@shopify/hydrogen-react";
import {CountryCode, LanguageCode} from "@shopify/hydrogen-react/dist/types/storefront-api-types";

interface Ingredient {
  id: number;
  handle: string;
  title: string;
  unitsFor2: string;
  image: string;
}

const sanitizeHandle = (handle: string) => {
  const sanitized = handle.replace(/[^\w_]/g, ""); // sanitize function
  return sanitized.replace(/^\d/, "_").trim();
};

const generateIngredientsQuery = (
  handles: string[],
  country: CountryCode,
  language: LanguageCode,
) => {
  let query = `#graphql
    fragment productFields on Product {
      handle
      title
      id
      unitsFor2: metafield(namespace: "recipe", key: "unitsfor2") {
        id
        value
      }
      featuredImage {
        url(transform: { maxWidth: 100, maxHeight: 100, scale: 2, crop: CENTER })
      }
    }

    query ingredientsQuery @inContext(country: ${country}, language: ${language}) {`;

  handles
  .filter((handle) => sanitizeHandle(handle) !== "")

  .forEach((handle) => {
    const sanitizedHandle = sanitizeHandle(handle);
    query += `
      ${sanitizedHandle}: product(handle: "${handle}") {
        ...productFields
      }`;
  });

  query += `}`;

  return query;
};

const useIngredientsFetch = (
  {ingredientHandles}: {ingredientHandles: string[]},
  options?: {enabled: boolean}
) => {
  if (
    (!options || options.enabled) &&
    (!ingredientHandles || ingredientHandles.length === 0)
  ) {
    throw new Error("ingredientHandles array is not set or empty");
  }

  const sFetch = useShopifyFetch();
  const {countryIsoCode, languageIsoCode} = useShop();

  const fetchIngredients = async () => {
    const {data} = await sFetch({
      query: generateIngredientsQuery(ingredientHandles, countryIsoCode, languageIsoCode),
    });

    const ingredients: Ingredient[] = ingredientHandles.map((handle) => {
      const sanitizedHandle = sanitizeHandle(handle); // Sanitize the handle
      const ingredientData = data[sanitizedHandle];

      if (!ingredientData) {
        return {
          handle,
          id: null,
          title: null, // Default value
          unitsFor2: null, // Default value
          image: null, // Default value
        };
      }

      return {
        id: parseInt(ingredientData.id.match(/\/(\d+)$/)[1]), //Extract the ID from the shopify "ID" path
        handle: ingredientData.handle,
        title: ingredientData.title,
        unitsFor2: ingredientData.unitsFor2?.value || "0", // Default to '0' if value is not present
        image: ingredientData.featuredImage?.url || window.brokenimageUrl, // Default to a placeholder URL if the image is not present
      };
    });

    return ingredients;
  };

  return useQuery(["ingredients", ingredientHandles], fetchIngredients, {
    staleTime: Infinity, // data is considered fresh for 1 day
    cacheTime: 1000 * 60 * 60 * 24 * 7, // data stays in cache for 7 days
    ...options,
  });
};

export default useIngredientsFetch;
